import {
  At,
  Bug,
  Gear,
  MagnifyingGlass,
  MapPin,
  MapTrifold,
  Plus,
  Question,
} from '@phosphor-icons/react';
import {selectIsAdmin} from '@smartmixin/core';
import {messages} from '@smartmixin/i18n';
import React from 'react';
import {useIntl} from 'react-intl';
import {useSelector} from 'react-redux';
import {
  type ConnectSourceState,
  connectSourceState,
} from '../../screens/settings/services/connectSource';
import {searchShortcut} from './useSearchShortcut';

export type NavItem = {
  key: string;
  label: string;
  icon: React.ReactNode;
  to: string;
  /** The phone bottom bar's label, where the full one does not fit. */
  shortLabel?: string;
  /** Whether `pathname` is this destination (aria-current="page"). */
  isActive: (pathname: string) => boolean;
  /** A keyboard shortcut to it: shown as a hint, set as aria-keyshortcuts. */
  keyShortcuts?: {label: string; aria: string};
  /** Router state the link sets (the GA connect source, #379). */
  state?: ConnectSourceState;
};

const startsWith = (prefix: string) => (p: string) => p.startsWith(prefix);
const never = () => false;
const isSearch = startsWith('/app/stations/search');
/** My weather's page, and the station and place pages it opens (FE #381). */
const isMyWeather = (p: string) =>
  p.startsWith('/app/places/') ||
  p === '/app' ||
  p === '/app/' ||
  (p.startsWith('/app/stations') && !isSearch(p));

/**
 * The one list of shell destinations, shared by the sidebar, the rail and
 * the phone bottom bar so they cannot drift. At most one item is active for
 * any path: search is carved out of stations, and "Add a station" (a
 * shortcut into Settings > Services) never claims the page from Settings.
 *
 * Forecast maps (#327 MR8, for everyone since P-B) are a mode of the one
 * Map entry, which also stays current on the pages forecast mode links to
 * (/forecast-maps/*: the animation, and the browse and builder pages behind
 * their flags); there is no separate area.
 *
 * My weather (FE #381 W2, everyone's with no flag) holds the Stations slot
 * (`stations`): /app/, current on the station pages too.
 */
export const useNavItems = () => {
  const {formatMessage} = useIntl();
  const isAdmin = useSelector(selectIsAdmin);

  return React.useMemo(() => {
    const search: NavItem = {
      key: 'search',
      // Short, so it fits the 232px sidebar in every locale: the page's own
      // title and field say what it searches ("Search for a place or
      // station"; Vincent, 2026-10-25).
      label: formatMessage(messages.common.search),
      icon: <MagnifyingGlass />,
      to: '/app/stations/search',
      isActive: isSearch,
      keyShortcuts: searchShortcut(),
    };
    const stations: NavItem = {
      key: 'stations',
      label: formatMessage(messages.stations.myWeather),
      shortLabel: formatMessage(messages.stations.myWeather),
      icon: <MapPin />,
      to: '/app/',
      isActive: isMyWeather,
    };
    const map: NavItem = {
      key: 'map',
      label: formatMessage(messages.weatherMap.weatherMap),
      shortLabel: formatMessage(messages.navigation.mapShort),
      icon: <MapTrifold />,
      to: '/map/',
      isActive: (p) => p.startsWith('/map') || p.startsWith('/forecast-maps'),
    };
    const help: NavItem = {
      key: 'help',
      label: formatMessage(messages.common.help),
      icon: <Question />,
      to: '/help',
      isActive: startsWith('/help'),
    };
    const settings: NavItem = {
      key: 'settings',
      label: formatMessage(messages.settings.settings),
      icon: <Gear />,
      to: '/app/settings/',
      isActive: startsWith('/app/settings'),
    };
    return {
      primary: [search, stations, map],
      help,
      admin: isAdmin
        ? [
            {
              key: 'debug',
              label: 'Debug',
              icon: <Bug />,
              to: '/app/admin/debug',
              isActive: startsWith('/app/admin'),
            },
          ]
        : [],
      footer: [
        {
          key: 'addStation',
          label: formatMessage(messages.stations.addAStation),
          icon: <Plus />,
          to: '/app/settings/services/',
          isActive: never,
          state: connectSourceState('drawer'),
        },
        settings,
        {
          key: 'contact',
          label: formatMessage(messages.settings.contactUs),
          icon: <At />,
          to: 'mailto:contact@smartmixin.io',
          isActive: never,
        },
      ] as NavItem[],
      settings,
      stations,
      map,
    };
  }, [formatMessage, isAdmin]);
};
